import { Dispatch, SetStateAction } from "react"
import { Card, CardContent } from "@components/common/card"
import { DictType } from "@pma/types"
import { useModal } from "@stores/modal-store"
import { Link } from "react-router-dom"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { Folder, Folders, LinkIcon, FolderTree } from "lucide-react"
import useTranslator from "@hooks/use-translator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { formatFolderSize, getSubFoldersCount } from "@pma/utils/helpers"

interface Props {
  folder: DictType
  setActiveFolder: Dispatch<SetStateAction<DictType>>
}

const FolderCard = ({ folder, setActiveFolder }: Props) => {
  const modal = useModal()
  const { translate, locale } = useTranslator()

  const getFolderDetails = () => {
    const details = []
    if (folder?._subfolders_count > 0) {
      const subFolderText = (
        <div className='inline-block items-center gap-2'>
          <div className='flex cursor-pointer items-center gap-2 text-primary hover:underline'>
            {folder?._subfolders_count || "---"} {translate("Sub Folders")}
          </div>
        </div>
      )
      details.push(subFolderText)
    } else {
      details.push(`0 ${translate("Sub Folders")}`)
    }

    if (folder.folder_size !== undefined) {
      details.push(`${formatFolderSize(folder.folder_size, translate)}`)
    }
    if (folder.files && folder.files.length > 0) {
      details.push(`${folder.files.length} ${translate("Files")}`)
    }

    const lastUpdated = folder.last_update
      ? new Date(folder.last_update).toLocaleDateString(locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : ""
    if (lastUpdated) {
      details.push(lastUpdated)
    }

    return details
  }

  const { total, data } = getSubFoldersCount(folder)
  const levelCount = Object.keys(data).length

  let IconComponent
  if (total === 0) {
    IconComponent = Folder
  } else if (levelCount === 2) {
    IconComponent = Folders
  } else {
    IconComponent = FolderTree
  }

  return (
    <div className='p-2 transition-all duration-200 ease-in-out md:hover:bg-muted/100'>
      <Card className='' key={folder?.id}>
        <CardContent
          className='cursor-pointer p-2'
          onClick={() => {
            modal.onOpen()
            setActiveFolder(folder)
            modal.setContext("folder")
          }}
        >
          <div className='relative flex h-28 items-center justify-center bg-muted/40'>
            <IconComponent className='h-12 w-12 text-muted-foreground' />
          </div>
          <div className='py-2 text-center'>
            <div className='flex max-w-full flex-col items-center justify-center gap-2 overflow-hidden text-base font-medium md:flex-row'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <h3 className='truncate'>{folder?.name || "---"}</h3>
                </TooltipTrigger>
                <TooltipContent align='start'>{folder?.name || "---"}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  {folder?.project_id ? (
                    <Link
                      to={PROJECTS_PAGE_ROUTE + `/${folder?.project_id}`}
                      className='hidden text-primary md:inline'
                    >
                      <LinkIcon size={14} />
                    </Link>
                  ) : null}
                </TooltipTrigger>
                <TooltipContent>{translate("Open project")}</TooltipContent>
              </Tooltip>
              <Link
                to={PROJECTS_PAGE_ROUTE + `/${folder?.project_id}`}
                className='text-xs text-primary md:hidden'
              >
                <LinkIcon size={14} className='inline' /> {translate("open project")}
              </Link>
            </div>
          </div>
          <div className='min-h-[2.5rem] text-center text-xs text-muted-foreground'>
            {getFolderDetails().map((detail, index) => (
              <span key={index}>
                {detail}
                {index < getFolderDetails().length - 1 && " | "}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default FolderCard
